/* eslint-disable @typescript-eslint/no-explicit-any */
import { createApi } from "@reduxjs/toolkit/query/react";
import { ApiResponse, Editor, User } from "../../types/apiTypes";
import { UsersTable } from "@/data/usersData";
import { normalBaseQuery } from "../BaseQuery";
import { companyApi } from "./companyApi";
import { vipApi } from "./vipApi";
export type AreaType = "company" | "vip";
export type AreaOfInterest = { _id: string; name: string; type_related: AreaType };
export type queriesType =
  | "companyTopSearch:public"
  | "companyTopSearch:private"
  | "companyTopSearch:nasdaq"
  | "vipTopSearch"
  | "hrTopSearch"
  | "allTopSearch"
  | "all";

export type DashboardSummary = {
  revenue: { value: number; changePercent: number };
  userLogins: { value: number; changePercent: number };
  newSubscribers: { value: number; changePercent: number };
  activeUsers: { value: number; changePercent: number };
};

export type ViewsByHourEntry = {
  hour: string;
  visits: number;
};

export type PerformanceOverview = {
  totalSearches24h: number;
  totalClicks24h: number;
};

export type TopCompanyView = {
  name: string;
  views: number;
};

export type TopEntityViews = {
  company: TopCompanyView[];
  vip: TopCompanyView[];
  hr: TopCompanyView[];
};

export type TopActiveUser = {
  name: string;
  actions: number;
  plan: string;
};

export type Alert = {
  id: string;
  type: "suspicious" | "security" | "unusual";
  title: string;
  description: string;
  timestamp: string;
  severity: "high" | "medium" | "low";
};

export type GeneralStats = {
  companiesTracked: number;
  totalSearches: number;
  totalPageViews: number;
};

export type TokenUsage = {
  thisMonth: {
    start: string;
    end: string;
    used: number;
  };
  previousMonth: {
    start: string;
    end: string;
    used: number;
  };
  expiredExhaustedUsers: {
    userId: string;
    name: string;
    email: string;
    subscription_exp?: string;
    tokensAvailable: number;
    usedThisMonth: number;
    usedTotal: number;
    vipUsedTotal: number;
    hrUsedTotal: number;
  }[];
};

export type QueryWithScore = {
  value: string;
  score: number;
};

export type paymentWithUser = {
  createdAt: string;
  _id: string;
  plan_id?: { duration_months: number };
  split_across_months?: number;
  total_sum: number;
  user_id: {
    email: string;
    given_name: string;
    surname: string;
    phone_number?: string;
  };
};

export type PaymentAttemptAdmin = {
  _id: string;
  user_id: string;
  identity_user_id?: string;
  plan_id: string;
  status: "clicked" | "checkout_ready" | "redirected" | "processing" | "review_required" | "succeeded" | "failed" | "abandoned";
  user_snapshot: {
    email: string;
    given_name: string;
    surname?: string;
    phone_number?: string;
  };
  plan_snapshot: {
    plan_name: string;
    amount: number;
    currency: string;
    duration_months: number;
  };
  clicked_at: string;
  checkout_created_at?: string;
  redirected_at?: string;
  gateway_returned_at?: string;
  webhook_received_at?: string;
  completed_at?: string;
  failed_at?: string;
  gateway_payment_id?: string;
  gateway_transaction_id?: string;
  gateway_status?: string;
  reconciliation_checked_at?: string;
  reconciliation_error?: string;
  confirmation_source?: "webhook" | "peach_search";
  failure_reason?: string;
  failure_alert_queued_at?: string;
  createdAt: string;
  updatedAt: string;
};

export const adminApi = createApi({
  reducerPath: "adminApi",
  baseQuery: normalBaseQuery,
  tagTypes: ["Editor", "User", "Area", 'userTokens'],
  endpoints: (builder) => ({
    getUsersStats: builder.query<ApiResponse<{ users: User[] }>, void>({
      query: () => ({
        url: "admin/user/stats",
        method: "GET",
      }),
    }),
    getUploadLinkReport: builder.mutation<
      ApiResponse<{ url: string; key: string }>,
      { file_type: string; year: number; company_name: string }
    >({
      query: ({ file_type, year, company_name }) => {
        return {
          url: "admin/files/signurl/report",
          method: "GET",
          params: {
            file_type,
            year,
            company_name,
          },
        };
      },
    }),
    getMultipulLinkReport: builder.mutation<
      ApiResponse<{ url: string; key: string; year: number }[]>,
      { file_type: string; year: number; company_name: string }[]
    >({
      query: (data) => {
        return {
          url: "admin/files/signurl/report",
          method: "POST",
          body: {
            reports: data,
          },
        };
      },
    }),
    getUserTokens: builder.query<ApiResponse<{ tokenAvail: number }[]>, { userId: string }>({
      query: ({ userId }) => ({
        url: `admin/token/${userId}`,
        method: "GET",
      }),
      providesTags:['userTokens']
    }),
    addUserTokens: builder.mutation<ApiResponse<any>, { userId: string; tokens: number }>({
      query: ({ userId, tokens }) => ({
        url: `admin/token/${userId}`,
        method: "PUT",
        body: {
          tokens,
        },
      }),
      invalidatesTags: ["User"],
    }),
    getUploadLinkLogo: builder.mutation<
      ApiResponse<{ url: string; key: string }>,
      { file_type: string; company_name: string }
    >({
      query: ({ file_type, company_name }) => {
        return {
          url: "admin/files/signurl/logo",
          method: "GET",
          params: {
            file_type,
            company_name,
          },
        };
      },
    }),
    getUploadLinkExcel: builder.query<ApiResponse<{ url: string; key: string }>, { file_type: string; model: string }>({
      query: ({ file_type, model }) => {
        return {
          url: "admin/files/signurl/excel",
          method: "GET",
          params: {
            file_type,
            model,
          },
        };
      },
    }),
    getAllEditors: builder.query<ApiResponse<{ users: Editor[]; hasMore: boolean }>, void>({
      providesTags: ["Editor"],
      query: () => ({
        url: "admin/user",
        method: "GET",
        params: {
          role: "admin",
        },
      }),
    }),
    getAllUsers: builder.query<ApiResponse<{ users: UsersTable[]; hasMore: boolean }>, void>({
      providesTags: ["User"],
      query: () => {
        return {
          url: "admin/user",
          method: "GET",
          params: {
            role: "user",
          },
        };
      },
    }),
    createEditor: builder.mutation<ApiResponse<{ editor: Editor }>, Editor>({
      invalidatesTags: ["Editor"],
      query: (editor) => ({
        url: "admin/user/add-admin",
        method: "POST",
        body: editor,
      }),
    }),
    banUser: builder.mutation<ApiResponse<any>, { id: string; baned: boolean }>({
      invalidatesTags: ["User"],
      query: ({ id, baned }) => {
        return {
          url: `admin/user/ban/${id}`,
          method: "PUT",
          body: {
            baned,
          },
        };
      },
    }),
    deleteUser: builder.mutation<ApiResponse<any>, { id: string }>({
      invalidatesTags: ["Editor", "User"],
      query: ({ id }) => ({
        url: `admin/user/${id}`,
        method: "DELETE",
      }),
    }),
    getTopQuereys: builder.query<ApiResponse<QueryWithScore[]>, { type: queriesType }>({
      query: ({ type }) => ({
        url: "company/queries",
        method: "GET",
        params: {
          type,
        },
      }),
    }),
    addSubscribation: builder.mutation<ApiResponse<any>, { planId: string; paymentId: string; userId: string, months?: number }>({
      query: ({ userId, planId, paymentId, months }) => ({
        url: `admin/subscribe/${userId}`,
        method: "POST",
        body: {
          planId,
          paymentId,
          months,
        },
      }),
      invalidatesTags: ["User"],
    }),
    getAllAreas: builder.query<ApiResponse<AreaOfInterest[]>, AreaType>({
      query: (type_related) => ({
        url: "admin/area",
        method: "GET",
        params: { type_related },
      }),
      providesTags: (_result, _error, type_related) => [{ type: "Area", id: type_related }],
    }),
    deleteArea: builder.mutation<ApiResponse<any>, { id: string; type_related: AreaType }>({
      query: ({ id, type_related }) => ({
        url: `admin/area/${id}`,
        method: "DELETE",
        params: { type_related },
      }),
      invalidatesTags: (_result, error, { type_related }) => error ? [] : [{ type: "Area", id: type_related }],
      async onQueryStarted({ type_related }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(type_related === "vip" ? vipApi.util.invalidateTags(["Area"]) : companyApi.util.invalidateTags(["Area"]));
        } catch { /* Keep the existing choices when saving fails. */ }
      },
    }),
    addArea: builder.mutation<ApiResponse<any>, { name: string; type_related: AreaType }>({
      query: (area) => ({
        url: "admin/area",
        method: "POST",
        body: area,
      }),
      invalidatesTags: (_result, error, { type_related }) => error ? [] : [{ type: "Area", id: type_related }],
      async onQueryStarted({ type_related }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(type_related === "vip" ? vipApi.util.invalidateTags(["Area"]) : companyApi.util.invalidateTags(["Area"]));
        } catch { /* Keep the existing choices when saving fails. */ }
      },
    }),
    getPaymentsWithUsers: builder.query<ApiResponse<paymentWithUser[]>, void>({
      query: () => ({
        url: "admin/analisis/payment-data/with_users",
        method: "GET",
      }),
    }),
    getPaymentAttempts: builder.query<ApiResponse<PaymentAttemptAdmin[]>, void>({
      query: () => ({
        url: "admin/analisis/payment-attempts",
        method: "GET",
      }),
    }),
    getDashboardSummary: builder.query<ApiResponse<DashboardSummary>, void>({
      query: () => ({
        url: "admin/analisis/dashboard-summary",
        method: "GET",
      }),
    }),
    getViewsByHour: builder.query<ApiResponse<ViewsByHourEntry[]>, void>({
      query: () => ({
        url: "admin/analisis/views-by-hour",
        method: "GET",
      }),
    }),
    getPerformanceOverview: builder.query<ApiResponse<PerformanceOverview>, void>({
      query: () => ({
        url: "admin/analisis/performance-overview",
        method: "GET",
      }),
    }),
    getTopCompanyViews: builder.query<ApiResponse<TopCompanyView[]>, void>({
      query: () => ({
        url: "admin/analisis/top-company-views",
        method: "GET",
      }),
    }),
    getTopEntityViews: builder.query<ApiResponse<TopEntityViews>, void>({
      query: () => ({
        url: "admin/analisis/top-entity-views",
        method: "GET",
      }),
    }),
    getTopActiveUsers: builder.query<ApiResponse<TopActiveUser[]>, void>({
      query: () => ({
        url: "admin/analisis/top-active-users",
        method: "GET",
      }),
    }),
    getAlerts: builder.query<ApiResponse<Alert[]>, void>({
      query: () => ({
        url: "admin/analisis/alerts",
        method: "GET",
      }),
    }),
    getGeneralStats: builder.query<ApiResponse<GeneralStats>, void>({
      query: () => ({
        url: "admin/analisis/general-stats",
        method: "GET",
      }),
    }),
    getTokenUsage: builder.query<ApiResponse<TokenUsage>, void>({
      query: () => ({
        url: "admin/analisis/token-usage",
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetUsersStatsQuery,
  useGetUploadLinkReportMutation,
  useGetUploadLinkLogoMutation,
  useGetAllAreasQuery,
  useDeleteAreaMutation,
  useAddAreaMutation,
  useGetPaymentsWithUsersQuery,
  useGetPaymentAttemptsQuery,
  useAddUserTokensMutation,
  useAddSubscribationMutation,
  useGetTopQuereysQuery,
  useGetMultipulLinkReportMutation,
  useGetUploadLinkExcelQuery,
  useLazyGetUploadLinkExcelQuery,
  useGetAllEditorsQuery,
  useCreateEditorMutation,
  useGetUserTokensQuery,
  useDeleteUserMutation,
  useGetAllUsersQuery,
  useBanUserMutation,
  useGetDashboardSummaryQuery,
  useGetViewsByHourQuery,
  useGetPerformanceOverviewQuery,
  useGetTopCompanyViewsQuery,
  useGetTopEntityViewsQuery,
  useGetTopActiveUsersQuery,
  useGetAlertsQuery,
  useGetGeneralStatsQuery,
  useGetTokenUsageQuery,
} = adminApi;
